/**
 * Reset Password Page
 *
 * Allows users to reset their password using a code sent by Cognito.
 */
import { useSearchParams } from 'react-router-dom';

import { useEffect, useState } from 'react';

import { useFormWithValidation } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  Alert,
  Card,
  FormButton,
  FormField,
  H1,
  H2,
  Text,
} from '@splitfiesta/ui';

import { AuthPageSkeleton } from '@/components/common/AuthPageSkeleton';
import {
  PasswordStrengthIndicator,
  validatePassword,
} from '@/components/common/PasswordStrengthIndicator';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { Footer } from '@/components/layout/Footer';

import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { t, ready } = useTranslation();
  const { handleError } = useErrorHandler('auth');
  const { resetPassword, isResettingPassword, resetPasswordError } = useAuth();

  // Load required namespaces
  const authLoaded = useNamespace('auth');
  const commonLoaded = useNamespace('common');
  const errorsLoaded = useNamespace('errors');

  // Form validation and submission
  const {
    handleSubmit: handleFormSubmit,
    isSubmitting,
    error,
    setError,
  } = useFormWithValidation({
    onSubmit: async () => {
      resetPassword({ email, code, newPassword });
    },
    validateFn: () => {
      if (!code.trim()) {
        return t('auth:errors.codeRequired');
      }
      if (code.length !== 6) {
        return t('auth:errors.invalidCode');
      }
      if (newPassword !== confirmPassword) {
        return t('auth:errors.passwordMismatch');
      }
      const { isValid, errors } = validatePassword(newPassword);
      if (!isValid) {
        return t(errors[0]);
      }
      return null;
    },
  });

  // Handle errors
  useEffect(() => {
    if (resetPasswordError) {
      setError(handleError(resetPasswordError));
    }
  }, [resetPasswordError, handleError, setError]);

  const isLoading = isSubmitting || isResettingPassword;

  // Show skeleton while translations load
  if (!ready || !authLoaded || !commonLoaded || !errorsLoaded) {
    return <AuthPageSkeleton fieldCount={3} />;
  }

  // Redirect if no email
  if (!email) {
    return (
      <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none flex flex-col">
        <div className="flex-1 p-4 flex items-center justify-center">
          <Card>
            <Alert
              type="error"
              message={t('auth:errors.invalidEmail')}
              className="mb-4"
            />
            <RouteChangeLink
              href="/forgot-password"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
            >
              {t('auth:forgotPassword.title')}
            </RouteChangeLink>
          </Card>
        </div>
        <Footer variant="minimal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none flex flex-col">
      <div className="flex-1 p-4 pt-4">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="text-center mb-8 mt-8">
            <RouteChangeLink href="/">
              <H1 className="text-white hover:text-white/90 transition-colors cursor-pointer drop-shadow-lg mb-2">
                {t('common:app.name')}
              </H1>
            </RouteChangeLink>
          </div>

          <Card>
            <div className="text-center mb-6">
              <H2>{t('auth:resetPassword.title')}</H2>
              <Text variant="bodySmall" className="mt-1">
                {t('auth:resetPassword.subtitle', { email })}
              </Text>
            </div>

            {error && (
              <Alert
                type="error"
                message={String(error)}
                className="mb-6"
                data-testid="reset-password-error-message"
              />
            )}

            <form
              onSubmit={e => handleFormSubmit(e, { code, newPassword })}
              className="space-y-4"
              data-testid="reset-password-form"
            >
              <FormField
                id="code"
                type="text"
                label={t('auth:resetPassword.codePlaceholder')}
                value={code}
                onChange={setCode}
                placeholder="123456"
                required
                autoFocus
                disabled={isLoading}
                maxLength={6}
                autoComplete="one-time-code"
                data-testid="reset-password-code-input"
              />

              <div className="space-y-2">
                <FormField
                  id="newPassword"
                  type="password"
                  label={t('auth:resetPassword.newPasswordPlaceholder')}
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder={t('auth:register.passwordPlaceholder')}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  data-testid="reset-password-new-password-input"
                />
                <PasswordStrengthIndicator
                  password={newPassword}
                  className="mt-2"
                  data-testid="reset-password-strength"
                />
              </div>

              <div className="space-y-2">
                <FormField
                  id="confirmPassword"
                  type="password"
                  label={t('auth:resetPassword.confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder={t('auth:register.confirmPasswordPlaceholder')}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  data-testid="reset-password-confirm-password-input"
                />
                {confirmPassword && (
                  <PasswordStrengthIndicator
                    password={newPassword}
                    confirmPassword={confirmPassword}
                    showConfirmMatch={true}
                    compact={true}
                    className="mt-2"
                    data-testid="reset-password-match-indicator"
                  />
                )}
              </div>

              <FormButton
                type="submit"
                loading={isLoading}
                loadingText={t('common:actions.loading')}
                fullWidth
                data-testid="reset-password-submit-button"
              >
                {t('auth:resetPassword.submit')}
              </FormButton>
            </form>

            <div className="mt-6 text-center">
              <RouteChangeLink
                href="/login"
                className="text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 text-sm"
                data-testid="back-to-login-link"
              >
                {t('auth:resetPassword.backToLogin')}
              </RouteChangeLink>
            </div>
          </Card>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}
